import { useState, useEffect } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useAuth, useRequireAuth } from '../../utils/auth'
import { tenantApi } from '../../utils/api'

export default function TenantSettings() {
  const { slug } = useParams()
  const navigate = useNavigate()
  const { tenant, logout } = useAuth()
  const { session, loading, authorized } = useRequireAuth('tenant')
  
  const [stripeLoading, setStripeLoading] = useState(false)

  const basePath = slug ? `/t/${slug}` : ''

  useEffect(() => {
    if (!loading && !authorized) {
      navigate(`${basePath}/admin`)
    }
  }, [loading, authorized, navigate, basePath])

  const handleStripeConnect = async () => {
    setStripeLoading(true)
    try {
      const data = await tenantApi.startStripeOnboard(
        window.location.origin + basePath + '/admin/settings?stripe=success',
        window.location.origin + basePath + '/admin/settings?stripe=refresh'
      )
      window.location.href = data.url
    } catch (err) {
      console.error('Failed to start Stripe onboarding:', err)
    } finally {
      setStripeLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-500" />
      </div>
    )
  }

  if (!authorized) return null

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      {/* Header */}
      <header className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            {tenant?.logoUrl ? (
              <img src={tenant.logoUrl} alt={tenant.name} className="h-8" />
            ) : tenant && (
              <div 
                className="w-8 h-8 rounded-lg flex items-center justify-center text-white font-bold"
                style={{ backgroundColor: tenant.primaryColor }}
              >
                {tenant.logoText || tenant.name.charAt(0)}
              </div>
            )}
            <h1 className="font-semibold text-gray-900 dark:text-white">{tenant?.name} Settings</h1>
          </div>
          
          <nav className="flex items-center gap-4">
            <Link to={`${basePath}/admin`} className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white text-sm">
              ← Back to Admin
            </Link>
            <button onClick={logout} className="btn-ghost text-sm">Sign Out</button>
          </nav>
        </div>
      </header>
      
      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-8 space-y-8">
        {/* Organization Info */}
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Organization</h2>
          <div className="grid sm:grid-cols-2 gap-6">
            <div>
              <label className="label">Organization Name</label>
              <p className="text-gray-900 dark:text-white font-medium">{tenant?.name}</p>
            </div>
            <div>
              <label className="label">Registration URL</label>
              <p className="text-gray-900 dark:text-white font-medium">cloudcamp.uk/t/{tenant?.slug}</p>
            </div>
            <div>
              <label className="label">Contact Email</label>
              <p className="text-gray-900 dark:text-white">{tenant?.contactEmail}</p>
            </div>
            <div>
              <label className="label">Currency</label>
              <p className="text-gray-900 dark:text-white uppercase">{tenant?.currency || 'USD'}</p>
            </div>
          </div>
        </div>
        
        {/* Branding */}
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Branding</h2>
          <div className="grid sm:grid-cols-2 gap-6">
            <div>
              <label className="label">Primary Color</label>
              <div className="flex items-center gap-3">
                <div 
                  className="w-10 h-10 rounded-lg border border-gray-200 dark:border-gray-700"
                  style={{ backgroundColor: tenant?.primaryColor }}
                />
                <span className="text-gray-900 dark:text-white font-mono">{tenant?.primaryColor}</span>
              </div>
            </div>
            <div>
              <label className="label">Logo</label>
              {tenant?.logoUrl ? (
                <img src={tenant.logoUrl} alt="Logo" className="h-10" />
              ) : (
                <p className="text-gray-500 dark:text-gray-400">Using text logo: "{tenant?.logoText || tenant?.name.charAt(0)}"</p>
              )}
            </div>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-4">
            Contact support to update branding settings.
          </p>
        </div>
        
        {/* Stripe Connect */}
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Payment Processing</h2>
          
          {tenant?.stripeOnboarded ? (
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                <svg className="w-6 h-6 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div>
                <p className="font-medium text-gray-900 dark:text-white">Stripe Connected</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Your Stripe account is connected and ready to accept payments.
                </p>
              </div>
            </div>
          ) : (
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-lg bg-yellow-100 dark:bg-yellow-900/30 flex items-center justify-center">
                <span className="text-2xl">💳</span>
              </div>
              <div className="flex-1">
                <p className="font-medium text-gray-900 dark:text-white mb-1">Connect Stripe</p>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                  Connect your Stripe account to accept payments from registrations. 
                  Funds will be deposited directly to your bank account.
                </p>
                <button 
                  onClick={handleStripeConnect}
                  disabled={stripeLoading}
                  className="btn-primary"
                  style={{ backgroundColor: tenant?.primaryColor }}
                >
                  {stripeLoading ? 'Loading...' : 'Connect with Stripe'}
                </button>
              </div>
            </div>
          )}
        </div>
        
        {/* Account Info */}
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Your Account</h2>
          <div className="grid sm:grid-cols-2 gap-6">
            <div>
              <label className="label">Email</label>
              <p className="text-gray-900 dark:text-white">{session?.email}</p>
            </div>
            <div>
              <label className="label">Role</label>
              <span className="badge-info capitalize">{session?.role}</span>
            </div>
          </div>
        </div>
        
        {/* Danger Zone */}
        <div className="card p-6 border-red-200 dark:border-red-900/50">
          <h2 className="text-lg font-semibold text-red-600 dark:text-red-400 mb-4">Danger Zone</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            These actions are irreversible. Please contact support if you need to make changes.
          </p>
          <button className="btn-danger opacity-50 cursor-not-allowed" disabled>
            Delete Organization
          </button>
        </div>
      </main>
    </div>
  )
}

